/* TheraTree product template JS
   Vanilla. No deps.
   Per-section concerns boot from within .tt-scope.
   Cross-section concerns (sticky cart, smooth scroll) boot once at document level.
   ---------------------------------------------------------------- */
(function () {
  'use strict';

  const ready = (fn) => (document.readyState !== 'loading') ? fn() : document.addEventListener('DOMContentLoaded', fn);
  let docBooted = false;

  // -------------------- Promise bar rotation (mobile) --------------------
  function initPromiseBar(root) {
    const list = root.querySelector('[data-tt-promise-list]');
    if (!list || list.__ttBound) return;
    list.__ttBound = true;
    const items = Array.from(list.querySelectorAll('[data-tt-promise-item]'));
    if (items.length < 2) return;

    const mq = window.matchMedia('(max-width: 767px)');
    let idx = 0;
    let timer = null;

    function show(i) {
      items.forEach((el, n) => el.classList.toggle('is-active', n === i));
    }
    function start() {
      stop();
      show(idx);
      timer = setInterval(() => {
        idx = (idx + 1) % items.length;
        show(idx);
      }, 4000);
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
      items.forEach((el) => el.classList.remove('is-active'));
    }
    function apply() { if (mq.matches) start(); else stop(); }
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply); else mq.addListener(apply);
  }

  // -------------------- Gallery thumbnails --------------------
  function initGallery(root) {
    const gallery = root.querySelector('[data-tt-gallery]');
    if (!gallery || gallery.__ttBound) return;
    gallery.__ttBound = true;
    const main = gallery.querySelector('[data-tt-gallery-main]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-tt-gallery-thumb]'));
    if (!main || thumbs.length < 2) return;

    thumbs.forEach((btn) => {
      btn.addEventListener('click', () => {
        const src = btn.getAttribute('data-src');
        const srcset = btn.getAttribute('data-srcset') || '';
        const alt = btn.getAttribute('data-alt') || '';
        if (!src) return;
        const img = main.querySelector('img');
        if (!img) return;
        img.src = src;
        if (srcset) img.srcset = srcset;
        img.alt = alt;
        thumbs.forEach((t) => t.setAttribute('aria-current', t === btn ? 'true' : 'false'));
      });
    });
  }

  // -------------------- Quantity stepper --------------------
  function initQty(root) {
    root.querySelectorAll('[data-tt-qty]').forEach((wrap) => {
      if (wrap.__ttBound) return;
      wrap.__ttBound = true;
      const input = wrap.querySelector('input[type="number"]');
      if (!input) return;
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      const form = input.closest('[data-tt-product-form]');
      wrap.querySelectorAll('[data-tt-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.getAttribute('data-tt-qty-step'), 10) || 1;
          let v = parseInt(input.value, 10) || min;
          v = Math.max(min, Math.min(max, v + step));
          input.value = String(v);
          input.dispatchEvent(new Event('change', { bubbles: true }));
          if (form) renderPrice(form, root);
        });
      });
      input.addEventListener('input', () => { if (form) renderPrice(form, root); });
      input.addEventListener('change', () => { if (form) renderPrice(form, root); });
    });
  }

  // -------------------- Shared price renderer --------------------
  // Single source of truth for the displayed price. Reads the per-unit cents
  // stored on the form (set by variant change or Seal price_update) and
  // multiplies by quantity before updating all display surfaces.
  function moneyStringToCents(str) {
    if (typeof str !== 'string') return null;
    const s = str.replace(/[^\d.,\-]/g, '').replace(/,/g, '');
    const n = parseFloat(s);
    return isNaN(n) ? null : Math.round(n * 100);
  }
  function getQty(form) {
    const inp = form.querySelector('input[name="quantity"]');
    if (!inp) return 1;
    return Math.max(1, parseInt(inp.value, 10) || 1);
  }
  function renderPrice(form, root) {
    if (!form) return;
    const unit = parseInt(form.getAttribute('data-tt-unit-cents') || '0', 10);
    if (!unit) return;
    const qty = getQty(form);
    const totalCents = unit * qty;
    const money = formatMoney(totalCents);
    const priceEl = root && root.querySelector('[data-tt-price-current]');
    if (priceEl) priceEl.textContent = money;
    const label = form.querySelector('[data-tt-add] [data-tt-add-label]')
               || form.querySelector('[data-tt-add-label]');
    const submit = form.querySelector('[data-tt-add]');
    if (label && !(submit && submit.disabled && /sold out/i.test(label.textContent))) {
      label.textContent = 'Add to cart · ' + money;
    }
    const stickyPrice = document.querySelector('.tt-stickycart__price');
    if (stickyPrice) stickyPrice.textContent = money;
    // compare-at also scales with qty for accurate strikethrough math
    const compareUnit = parseInt(form.getAttribute('data-tt-compare-cents') || '0', 10);
    const compareEl = root && root.querySelector('[data-tt-price-compare]');
    if (compareEl) {
      if (compareUnit && compareUnit > unit) {
        compareEl.textContent = formatMoney(compareUnit * qty);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  // -------------------- Variant selector --------------------
  function initVariants(root) {
    const form = root.querySelector('[data-tt-product-form]');
    if (!form || form.__ttVariantsBound) return;
    form.__ttVariantsBound = true;
    const variantInput = form.querySelector('input[name="id"]');
    const variantData = form.querySelector('[data-tt-variants-json]');
    if (!variantInput || !variantData) return;

    let variants;
    try { variants = JSON.parse(variantData.textContent); } catch (e) { return; }

    function selected() {
      const choices = Array.from(form.querySelectorAll('[data-tt-option][aria-pressed="true"]'))
        .map((el) => el.getAttribute('data-value'));
      return variants.find((v) => v.options.every((o, i) => o === choices[i]));
    }

    function update() {
      const variant = selected();
      const submit = form.querySelector('[data-tt-add]');
      if (variant) {
        variantInput.value = String(variant.id);
        // Store per-unit cents on the form so renderPrice can multiply by qty
        form.setAttribute('data-tt-unit-cents', String(variant.price));
        form.setAttribute('data-tt-compare-cents', String(variant.compare_at_price || 0));
        if (submit) submit.disabled = !variant.available;
        if (submit && !variant.available) {
          const label = submit.querySelector('[data-tt-add-label]');
          if (label) label.textContent = 'Sold out';
        }
        renderPrice(form, root);
      } else if (submit) {
        submit.disabled = true;
      }
    }

    form.querySelectorAll('[data-tt-option]').forEach((opt) => {
      opt.addEventListener('click', () => {
        const optionIndex = opt.getAttribute('data-option-index');
        form.querySelectorAll(`[data-tt-option][data-option-index="${optionIndex}"]`)
          .forEach((o) => o.setAttribute('aria-pressed', o === opt ? 'true' : 'false'));
        update();
      });
    });
    update();
  }

  function formatMoney(cents) {
    const tpl = (window.Shopify && Shopify.money_format) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    if (tpl.indexOf('{{amount}}') !== -1) return tpl.replace('{{amount}}', value);
    if (tpl.indexOf('{{amount_no_decimals}}') !== -1) return tpl.replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
    return `$${value}`;
  }

  // -------------------- Add to cart --------------------
  function initAddToCart(root) {
    const form = root.querySelector('[data-tt-product-form]');
    if (!form || form.__ttCartBound) return;
    form.__ttCartBound = true;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-tt-add]');
      if (!btn || btn.disabled) return;
      const originalLabel = btn.innerHTML;
      btn.setAttribute('aria-busy', 'true');
      btn.innerHTML = '<span class="tt-sr">Adding</span>Adding…';

      try {
        const cartEl = getCartElement();
        const formData = new FormData(form);
        formData.set('sections_url', window.location.pathname);
        const sectionsToRender = readCartSections(cartEl);
        if (sectionsToRender.length) formData.set('sections', sectionsToRender.join(','));
        if (cartEl && typeof cartEl.setActiveElement === 'function') {
          cartEl.setActiveElement(document.activeElement);
        }

        const routes = (window.routes && window.routes.cart_add_url)
          ? window.routes.cart_add_url
          : `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add.js`;

        const res = await fetch(routes, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        const data = await res.json();
        if (!res.ok || data.status) {
          const msg = (data && (data.description || data.message)) || 'Could not add to cart.';
          showError(form, msg);
          if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
            try { window.publish(window.PUB_SUB_EVENTS.cartError, { source: 'tt-product-form', productVariantId: formData.get('id'), errors: data.errors || data.description, message: data.message }); } catch (e) {}
          }
          return;
        }

        publishCart(data, cartEl);
      } catch (err) {
        showError(form, 'Network error. Please try again.');
      } finally {
        btn.removeAttribute('aria-busy');
        btn.innerHTML = originalLabel;
      }
    });
  }

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function readCartSections(cartEl) {
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      try {
        return cartEl.getSectionsToRender().map((s) => s.id);
      } catch (e) {}
    }
    // Fallback: probe for common Dawn/Refresh section ids in DOM
    const ids = new Set();
    document.querySelectorAll('cart-drawer, cart-notification, cart-icon-bubble').forEach((el) => {
      const id = (el.tagName || '').toLowerCase();
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }

  function publishCart(addResponse, cartEl) {
    if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
      try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'tt-product-form', productVariantId: addResponse.variant_id, cartData: addResponse }); } catch (e) {}
    }
    if (cartEl && typeof cartEl.renderContents === 'function' && addResponse.sections) {
      cartEl.renderContents(addResponse);
      if (cartEl.classList && cartEl.classList.contains('is-empty')) cartEl.classList.remove('is-empty');
    } else if (cartEl && typeof cartEl.open === 'function') {
      cartEl.open();
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: addResponse }));
    }
  }

  function showError(form, msg) {
    let el = form.querySelector('[data-tt-error]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-tt-error', '');
      el.setAttribute('role', 'alert');
      el.style.cssText = 'margin-top:.5rem;color:#c46a52;font-size:.8125rem;';
      form.appendChild(el);
    }
    el.textContent = msg;
  }

  // -------------------- Seal Subscriptions price sync --------------------
  // Listens to Seal's official `sealsubs:price_update` event and updates the
  // per-unit price stored on the form. The shared renderer then re-renders
  // total = unit × qty across all surfaces (price text, button label, sticky bar).
  //   https://www.sealsubscriptions.com/articles/merchant-javascript-api-documentation
  function initSealPriceSync(root) {
    const form = root.querySelector('[data-tt-product-form]');
    if (!form || form.__ttSealBound) return;
    form.__ttSealBound = true;

    function toCents(value) {
      if (typeof value === 'number') return Math.round(value);
      if (typeof value === 'string') return moneyStringToCents(value);
      return null;
    }

    document.addEventListener('sealsubs:price_update', (e) => {
      const detail = (e && e.detail) || {};
      const unitCents = toCents(detail.price);
      if (unitCents && unitCents > 0) {
        form.setAttribute('data-tt-unit-cents', String(unitCents));
      }
      const compareCents = toCents(detail.compareAtPrice);
      if (compareCents && compareCents > 0) {
        form.setAttribute('data-tt-compare-cents', String(compareCents));
      }
      renderPrice(form, root);
    });
  }

  // -------------------- Sticky cart bar (document-level singleton) --------------------
  function initStickyCart() {
    const bar = document.querySelector('[data-tt-stickycart]');
    const trigger = document.querySelector('[data-tt-stickycart-trigger]');
    if (!bar || !trigger || bar.__ttBound) return;
    bar.__ttBound = true;

    if (!('IntersectionObserver' in window)) {
      bar.classList.add('is-visible');
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { rootMargin: '0px', threshold: 0 });
    io.observe(trigger);

    const stickyBtn = bar.querySelector('[data-tt-stickycart-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = document.querySelector('[data-tt-product-form]');
        if (!form) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }
  }

  // -------------------- Video modal (click to play full with audio) --------------------
  let vidModal = null;
  function openVideoModal(src) {
    if (!src) return;
    if (!vidModal) {
      vidModal = document.createElement('div');
      vidModal.className = 'tt-vidmodal';
      vidModal.setAttribute('role', 'dialog');
      vidModal.setAttribute('aria-modal', 'true');
      vidModal.setAttribute('aria-label', 'Video player');
      vidModal.innerHTML = '<button type="button" class="tt-vidmodal__close" aria-label="Close video">×</button><video class="tt-vidmodal__video" controls playsinline></video>';
      document.body.appendChild(vidModal);
      vidModal.addEventListener('click', (e) => {
        if (e.target === vidModal || e.target.classList.contains('tt-vidmodal__close')) closeVideoModal();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && vidModal && vidModal.classList.contains('is-open')) closeVideoModal();
      });
    }
    const video = vidModal.querySelector('video');
    video.src = src;
    video.currentTime = 0;
    video.muted = false;
    video.volume = 1;
    vidModal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    video.play().catch(() => {
      // Some browsers block unmuted autoplay; user can press play via the controls
    });
  }
  function closeVideoModal() {
    if (!vidModal) return;
    const video = vidModal.querySelector('video');
    if (video) { try { video.pause(); video.removeAttribute('src'); video.load(); } catch (e) {} }
    vidModal.classList.remove('is-open');
    document.body.style.overflow = '';
  }
  function initVideoClicks() {
    document.querySelectorAll('[data-tt-video-src]').forEach((el) => {
      if (el.__ttVidBound) return;
      el.__ttVidBound = true;
      el.addEventListener('click', (e) => {
        e.preventDefault();
        openVideoModal(el.getAttribute('data-tt-video-src'));
      });
    });
  }

  // -------------------- Smooth scroll links (document-level) --------------------
  function initScrollLinks() {
    document.querySelectorAll('[data-tt-scrollto]').forEach((a) => {
      if (a.__ttBound) return;
      a.__ttBound = true;
      a.addEventListener('click', (e) => {
        const sel = a.getAttribute('data-tt-scrollto');
        const target = document.querySelector(sel);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // -------------------- Bootstrap --------------------
  function bootScope(scope) {
    if (!scope || scope.__ttBooted) return;
    scope.__ttBooted = true;
    initPromiseBar(scope);
    initGallery(scope);
    initQty(scope);
    initVariants(scope);
    initAddToCart(scope);
    initSealPriceSync(scope);
  }

  function bootDocument() {
    document.querySelectorAll('.tt-scope').forEach(bootScope);
    initStickyCart();
    initScrollLinks();
    initVideoClicks();
    docBooted = true;
  }

  ready(bootDocument);

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target;
    if (!root) return;
    const scopes = root.classList && root.classList.contains('tt-scope')
      ? [root]
      : Array.from(root.querySelectorAll('.tt-scope'));
    scopes.forEach(bootScope);
    initStickyCart();
    initScrollLinks();
    initVideoClicks();
  });
})();
